<template>
        <template v-if="$route.name === 'users'">

  <div class="container-fluid py-4">
    <UserFilter
      :filters="userFilters"
      :roles="rolesList"
      @update:filters="updateUserFilters"
      @apply-filters="fetchUsers"
      @reset-filters="resetUserFilters"
      @add="openAddUserModal"
    />


    <!-- User Table -->
    <UserTable
      :users="paginatedUsers"
      :loading="isLoading"
      @delete="handleDelete"
      @edit="handleEdit"
      class="mt-4"
    />
        <!-- Pagination Component -->
        <Pagination
        v-if="filteredUsers.length > itemsPerPage"
        :pagination="{
        currentPage: currentPage,
        totalPages: totalPages,
        totalItems: filteredUsers.length,
        from: (currentPage - 1) * itemsPerPage + 1,
        to: Math.min(currentPage * itemsPerPage, filteredUsers.length)
      }"
      @page-changed="handlePageChange"
    />
  </div>
  </template>
  <router-view></router-view>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import UserTable from "@/components/features/user/table/UserTable.vue";
import UserFilter from "@/components/features/user/filters/UserFilter.vue";
import Pagination from "@/components/common/PaginationControls.vue";

const store = useStore();
const router = useRouter();
const isLoading = ref(false);
const currentPage = ref(1);
const itemsPerPage = ref(10);
let searchDebounceToken = null; 


const totalPages = computed(() => 
  Math.ceil(filteredUsers.value.length / itemsPerPage.value)
);
// State
const userFilters = ref({
  role_id: "",
  gender: "",
  search: "",
});

// Computed properties
const users = computed(() => {
  const storeUsers = store.getters.getUsers;
  return Array.isArray(storeUsers) ? storeUsers : [];
});

const rolesList = computed(() => store.getters.getRoles || []);



const filteredUsers = computed(() => {
  return users.value.filter((user) => {
    const roleFilter = userFilters.value.role_id;
    const matchesRole =
      !roleFilter ||
      user.role_id == roleFilter ||
      (user.role && user.role.id == roleFilter);

    const matchesGender =
      !userFilters.value.gender || user.gender === userFilters.value.gender;
    const matchesSearch =
      !userFilters.value.search ||
      user.name
        ?.toLowerCase()
        .includes(userFilters.value.search.toLowerCase()) ||
      user.email
        ?.toLowerCase()
        .includes(userFilters.value.search.toLowerCase());

    return matchesRole && matchesGender && matchesSearch;
  });
});


const paginatedUsers = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage.value;
  const end = start + itemsPerPage.value;
  return filteredUsers.value.slice(start, end);
});
const handlePageChange = (page) => {
  currentPage.value = page;

};
// Methods
const updateUserFilters = (newFilters) => {
  if (searchDebounceToken) {
    searchDebounceToken.cancel();
  }
  userFilters.value = newFilters;
  currentPage.value = 1;
};

const fetchUsers = async () => {
  isLoading.value = true;
  try {
    await store.dispatch("fetchUsers", {
      role_id: userFilters.value.role_id,
      gender: userFilters.value.gender,
      search: userFilters.value.search,
    });
  } catch (err) {
    console.error("Failed to fetch users:", err);
  } finally {
    isLoading.value = false;
  }
};

const resetUserFilters = () => {
  if (searchDebounceToken) {
    searchDebounceToken.cancel();
  }
  userFilters.value = {
    role_id: "",
    gender: "",
    search: "",
  };
  currentPage.value = 1;
  fetchUsers();
};




const deleteUser = async (userId) => {
  try {
    await store.dispatch("deleteUser", userId);
    await fetchUsers();
  } catch (err) {
    console.error("Failed to delete user:", err);
  }
};

const handleDelete = (userId) => {
  deleteUser(userId);
};

const handleEdit = (userId) => {
  router.push(`/users/${userId}/edit`);
};

const openAddUserModal = () => {
  router.push("/users/add");
};

onMounted(() => {
  fetchUsers();
});
</script>
